<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { isClient } from '@/composables/useSSR'
import { splitStatusClass, itemStatusClass } from '@/composables/useAccountSplitStatus'
import { STATUS_CODES } from '@/constants'
import { formatTimestampSeconds } from '@/utils'
import {
  AccountSplit,
  type AccountSplit as AccountSplitType
} from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'
import AccountSplitDryRunDialog from './AccountSplitDryRunDialog.vue'

defineOptions({ name: 'AccountSplitDetail' })

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const notifyStore = useNotifyStore()

const splitId = computed(() => Number(route.params.id))

const data = reactive({
  loading: false,
  split: null as AccountSplitType | null,
  expandedItems: new Set<number>(),
  showAbort: false,
  abortReason: '',
  abortLoading: false,
  resumeLoading: false,
  showDryRun: false
})

const pollHandle = ref<ReturnType<typeof setInterval> | null>(null)

const POLLING_INTERVAL_MS = 3000

const stopPolling = () => {
  if (pollHandle.value !== null) {
    clearInterval(pollHandle.value)
    pollHandle.value = null
  }
}

const startPolling = () => {
  if (!isClient) return
  if (pollHandle.value !== null) return
  pollHandle.value = setInterval(() => {
    loadSplit(true)
  }, POLLING_INTERVAL_MS)
}

const ensurePollingMatchesStatus = () => {
  if (!data.split) {
    stopPolling()
    return
  }
  if (data.split.status === 'executing') {
    startPolling()
  } else {
    stopPolling()
  }
}

const loadSplit = async (silent = false) => {
  if (!silent) data.loading = true
  try {
    const res = await useAxios<null, AccountSplitType>(
      'GET',
      `/admin/account-splits/${splitId.value}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AccountSplit.safeParse(res.data)
      data.split = validate.success ? validate.data : (res.data as AccountSplitType)
      ensurePollingMatchesStatus()
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitDetail:load' })
  } finally {
    if (!silent) data.loading = false
  }
}

const formatDate = (secs: number | null | undefined): string =>
  formatTimestampSeconds(secs, 'dd.MM.yyyy HH:mm:ss')

const canResume = computed(() => data.split?.status === 'partial_failure')
const canAbort = computed(() => {
  const s = data.split?.status
  return s === 'draft' || s === 'dry_run_ok' || s === 'partial_failure'
})
const canReopenDryRun = computed(() => {
  return data.split?.status === 'dry_run_ok' && !!data.split.dry_run_result
})

const toggleItem = (id: number) => {
  if (data.expandedItems.has(id)) {
    const next = new Set(data.expandedItems)
    next.delete(id)
    data.expandedItems = next
  } else {
    data.expandedItems = new Set([...data.expandedItems, id])
  }
}

const itemRows = () => data.split?.items ?? []

const resumeSplit = async () => {
  if (!data.split || data.resumeLoading) return
  data.resumeLoading = true
  try {
    const res = await useAxios<null, { status: string }>(
      'POST',
      `/admin/account-splits/${data.split.id}/resume`,
      true
    )
    if (res && (res.status === STATUS_CODES.OK || res.status === STATUS_CODES.ACCEPTED)) {
      const finalStatus = res.data?.status
      const isPartial = finalStatus === 'partial_failure'
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: isPartial ? 'warning' : 'success',
        title: t('common.success'),
        message: isPartial
          ? t('dashboard.admin.accountSplit.notify.executePartial')
          : finalStatus === 'completed'
            ? t('dashboard.admin.accountSplit.notify.executeCompleted')
            : t('dashboard.admin.accountSplit.notify.resumeStarted')
      })
      await loadSplit(true)
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitDetail:resume' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error as Error)
    })
  } finally {
    data.resumeLoading = false
  }
}

const openAbortDialog = () => {
  data.abortReason = ''
  data.showAbort = true
}

const closeAbortDialog = () => {
  if (data.abortLoading) return
  data.showAbort = false
}

const confirmAbort = async () => {
  if (!data.split || data.abortLoading) return
  data.abortLoading = true
  try {
    const body: { reason?: string } = {}
    if (data.abortReason.trim()) body.reason = data.abortReason.trim()
    const res = await useAxios<{ reason?: string }, AccountSplitType>(
      'POST',
      `/admin/account-splits/${data.split.id}/abort`,
      true,
      body
    )
    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('common.success'),
        message: t('dashboard.admin.accountSplit.notify.aborted')
      })
      data.showAbort = false
      await loadSplit(true)
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitDetail:abort' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(error as Error)
    })
  } finally {
    data.abortLoading = false
  }
}

const reopenDryRun = () => {
  if (!canReopenDryRun.value) return
  data.showDryRun = true
}

const handleDryRunClose = () => {
  data.showDryRun = false
}

const handleExecuted = () => {
  // Refresh after execute kickoff
  loadSplit(true)
}

const goBack = () => router.push({ name: 'dashboardAdminAccountSplit' })

const handleVisibility = () => {
  if (!isClient) return
  if (document.visibilityState === 'hidden') {
    stopPolling()
  } else if (data.split?.status === 'executing') {
    startPolling()
  }
}

onMounted(() => {
  loadSplit()
  if (isClient) {
    document.addEventListener('visibilitychange', handleVisibility)
  }
})

onBeforeUnmount(() => {
  stopPolling()
  if (isClient) {
    document.removeEventListener('visibilitychange', handleVisibility)
  }
})

onBeforeRouteLeave(() => {
  stopPolling()
})
</script>

<template>
  <BaseDashboardPage
    :title="data.split ? `#${data.split.id}` : `#${splitId}`"
    :subtitle="$t('dashboard.admin.accountSplit.title')"
    icon="mdi-call-split"
    icon-color="purple"
    show-back
    @back="goBack"
  >
    <template #actions>
      <div class="header-actions">
        <button
          v-if="canReopenDryRun"
          class="btn-text"
          @click="reopenDryRun"
        >
          <v-icon icon="mdi-clipboard-text-search-outline" size="16" />
          <span>{{ $t('dashboard.admin.accountSplit.actions.reopenDryRun') }}</span>
        </button>
        <button
          v-if="canResume"
          class="btn-text"
          :disabled="data.resumeLoading"
          @click="resumeSplit"
        >
          <v-progress-circular
            v-if="data.resumeLoading"
            indeterminate
            size="14"
            width="2"
            color="#00B0FF"
          />
          <template v-else>
            <v-icon icon="mdi-play-outline" size="16" />
            <span>{{ $t('dashboard.admin.accountSplit.actions.resume') }}</span>
          </template>
        </button>
        <button
          v-if="canAbort"
          class="btn-danger"
          @click="openAbortDialog"
        >
          <v-icon icon="mdi-stop-circle-outline" size="16" />
          <span>{{ $t('dashboard.admin.accountSplit.actions.abort') }}</span>
        </button>
      </div>
    </template>

    <!-- Loading -->
    <div v-if="data.loading && !data.split" class="state-box">
      <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
    </div>

    <template v-else-if="data.split">
      <!-- Polling notice -->
      <div v-if="data.split.status === 'executing'" class="info-box info-box--warning">
        <v-icon icon="mdi-progress-clock" size="18" />
        <p>{{ $t('dashboard.admin.accountSplit.detail.pollingNotice') }}</p>
      </div>

      <!-- Metadata card -->
      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title">{{ $t('dashboard.admin.accountSplit.detail.sectionMeta') }}</h3>
          <span class="status-chip" :class="splitStatusClass(data.split.status)">
            {{ $t(`dashboard.admin.accountSplit.status.${data.split.status}`) }}
          </span>
        </div>

        <div class="kv-list">
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.admin.accountSplit.detail.sourceAccount') }}</span>
            <span class="kv-value mono">
              {{ data.split.source_account?.email || `#${data.split.source_account?.id ?? '?'}` }}
            </span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.admin.accountSplit.detail.targetAccount') }}</span>
            <span class="kv-value mono">
              <template v-if="data.split.target_account">
                {{ data.split.target_account.email || `#${data.split.target_account.id}` }}
              </template>
              <template v-else>—</template>
              <span class="kv-meta">({{ $t(`dashboard.admin.accountSplit.targetMode.${data.split.target_mode}`) }})</span>
            </span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.admin.accountSplit.detail.createdAt') }}</span>
            <span class="kv-value">{{ formatDate(data.split.created) }}</span>
          </div>
          <div class="kv-row">
            <span class="kv-label">{{ $t('dashboard.admin.accountSplit.detail.updatedAt') }}</span>
            <span class="kv-value">{{ formatDate(data.split.updated) }}</span>
          </div>
          <div v-if="data.split.executed_at" class="kv-row">
            <span class="kv-label">{{ $t('dashboard.admin.accountSplit.detail.executedAt') }}</span>
            <span class="kv-value">{{ formatDate(data.split.executed_at) }}</span>
          </div>
          <div v-if="data.split.notes" class="kv-row">
            <span class="kv-label">{{ $t('dashboard.admin.accountSplit.detail.notes') }}</span>
            <span class="kv-value">{{ data.split.notes }}</span>
          </div>
          <div v-if="data.split.failure_reason" class="kv-row kv-row--error">
            <span class="kv-label">{{ $t('dashboard.admin.accountSplit.detail.failureReason') }}</span>
            <span class="kv-value">{{ data.split.failure_reason }}</span>
          </div>
        </div>

        <div v-if="data.split.new_account_payload" class="snapshot-block">
          <h4 class="snapshot-title">{{ $t('dashboard.admin.accountSplit.detail.newAccountPayload') }}</h4>
          <div class="kv-list snapshot-list">
            <div
              v-for="(value, key) in (data.split.new_account_payload as Record<string, unknown>)"
              :key="key"
              class="kv-row"
            >
              <span class="kv-label mono">{{ key }}</span>
              <span class="kv-value mono">{{ String(value ?? '—') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Items -->
      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title">{{ $t('dashboard.admin.accountSplit.detail.sectionItems') }}</h3>
        </div>

        <div v-if="itemRows().length === 0" class="state-box">
          <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.detail.noItems') }}</p>
        </div>
        <div v-else class="data-table">
          <div class="table-header item-cols">
            <span class="col">{{ $t('dashboard.admin.accountSplit.detail.columnId') }}</span>
            <span class="col">{{ $t('dashboard.admin.accountSplit.detail.columnType') }}</span>
            <span class="col">{{ $t('dashboard.admin.accountSplit.detail.columnAssetId') }}</span>
            <span class="col">{{ $t('dashboard.admin.accountSplit.home.columnStatus') }}</span>
            <span class="col">{{ $t('dashboard.admin.accountSplit.detail.columnAttempts') }}</span>
            <span class="col">{{ $t('dashboard.admin.accountSplit.detail.columnStarted') }}</span>
            <span class="col">{{ $t('dashboard.admin.accountSplit.detail.columnFinished') }}</span>
          </div>
          <template v-for="item in itemRows()" :key="item.id">
            <div
              class="table-row item-cols"
              :class="{ expanded: data.expandedItems.has(item.id), failed: item.status === 'failed' }"
              @click="toggleItem(item.id)"
            >
              <span class="col mono">#{{ item.id }}</span>
              <span class="col">{{ $t(`dashboard.admin.accountSplit.assetType.${item.asset_type}`) }}</span>
              <span class="col mono">#{{ item.asset_id }}</span>
              <span class="col">
                <span class="status-chip" :class="itemStatusClass(item.status)">
                  {{ $t(`dashboard.admin.accountSplit.itemStatus.${item.status}`) }}
                </span>
              </span>
              <span class="col mono">{{ item.attempts }}</span>
              <span class="col">{{ formatDate(item.started_at) }}</span>
              <span class="col">{{ formatDate(item.finished_at) }}</span>
            </div>

            <div v-if="data.expandedItems.has(item.id)" class="item-expanded">
              <div v-if="item.error" class="item-error">
                <v-icon icon="mdi-alert-circle-outline" size="16" />
                <span>{{ item.error }}</span>
              </div>

              <div class="snapshot-pair">
                <div class="snapshot-col">
                  <h4 class="snapshot-title">{{ $t('dashboard.admin.accountSplit.detail.beforeSnapshot') }}</h4>
                  <div v-if="item.before_snapshot" class="kv-list snapshot-list">
                    <div
                      v-for="(v, k) in (item.before_snapshot as Record<string, unknown>)"
                      :key="k"
                      class="kv-row"
                    >
                      <span class="kv-label mono">{{ k }}</span>
                      <span class="kv-value mono">{{ String(v ?? '—') }}</span>
                    </div>
                  </div>
                  <p v-else class="empty-hint">—</p>
                </div>
                <div class="snapshot-col">
                  <h4 class="snapshot-title">{{ $t('dashboard.admin.accountSplit.detail.afterSnapshot') }}</h4>
                  <div v-if="item.after_snapshot" class="kv-list snapshot-list">
                    <div
                      v-for="(v, k) in (item.after_snapshot as Record<string, unknown>)"
                      :key="k"
                      class="kv-row"
                    >
                      <span class="kv-label mono">{{ k }}</span>
                      <span class="kv-value mono">{{ String(v ?? '—') }}</span>
                    </div>
                  </div>
                  <p v-else class="empty-hint">—</p>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <!-- Abort dialog -->
    <v-dialog v-model="data.showAbort" max-width="480" persistent>
      <div class="modal modal--danger">
        <div class="modal-header">
          <v-icon icon="mdi-stop-circle-outline" size="22" class="header-icon" />
          <h3 class="modal-title">{{ $t('dashboard.admin.accountSplit.actions.abort') }}</h3>
        </div>
        <div class="modal-content">
          <p class="modal-message">{{ $t('dashboard.admin.accountSplit.confirm.abort') }}</p>
          <label class="form-label">{{ $t('dashboard.admin.accountSplit.confirm.abortReason') }}</label>
          <textarea
            v-model="data.abortReason"
            class="form-textarea"
            rows="3"
            placeholder="Reason"
          />
        </div>
        <div class="modal-footer">
          <button class="btn-text" :disabled="data.abortLoading" @click="closeAbortDialog">
            {{ $t('dashboard.admin.accountSplit.actions.cancel') }}
          </button>
          <button class="btn-danger" :disabled="data.abortLoading" @click="confirmAbort">
            <v-progress-circular
              v-if="data.abortLoading"
              indeterminate
              size="14"
              width="2"
              color="#ef5350"
            />
            <template v-else>
              <v-icon icon="mdi-stop-circle-outline" size="16" />
              <span>{{ $t('dashboard.admin.accountSplit.actions.abort') }}</span>
            </template>
          </button>
        </div>
      </div>
    </v-dialog>

    <AccountSplitDryRunDialog
      :show="data.showDryRun"
      :split-id="data.split?.id ?? null"
      :dry-run-result="data.split?.dry_run_result ?? null"
      @close="handleDryRunClose"
      @executed="handleExecuted"
    />
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}

.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.85);
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(244, 67, 54, 0.08);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 8px;
  color: #ef5350;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.status-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ef5350;
}

.status-info {
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.3);
  color: #4fc3f7;
}

.status-grey {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.55);
}

.kv-list {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.06);
  border-radius: 10px;
}

.kv-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  &--error {
    .kv-label {
      color: rgba(239, 83, 80, 0.85);
    }
    .kv-value {
      color: #ef5350;
    }
  }
}

.kv-label {
  color: rgba(200, 218, 255, 0.55);
  font-size: 0.83rem;
}

.kv-value {
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-word;
}

.kv-meta {
  margin-left: 8px;
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.55);
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.84rem;
}

.snapshot-block {
  margin-top: 14px;
}

.snapshot-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.7);
  margin: 0 0 8px;
}

.snapshot-list .kv-row {
  grid-template-columns: 180px 1fr;
  padding: 8px 12px;
}

.data-table {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  overflow-x: auto;
}

.table-header,
.table-row {
  display: grid;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
}

.item-cols {
  grid-template-columns: 60px 130px 100px 130px 80px 150px 150px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.04);
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }

  &.expanded {
    background: rgba(120, 163, 255, 0.06);
  }

  &.failed:not(.expanded) {
    background: rgba(244, 67, 54, 0.04);
  }
}

.item-expanded {
  padding: 14px;
  background: rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid rgba(120, 163, 255, 0.04);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.item-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(244, 67, 54, 0.06);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 8px;
  color: #ef5350;
  font-size: 0.85rem;

  .v-icon {
    margin-top: 1px;
  }
}

.snapshot-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.snapshot-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px;
  text-align: center;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.info-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;

  p {
    margin: 0;
    font-size: 0.88rem;
  }

  &--warning {
    background: rgba(255, 183, 77, 0.08);
    border: 1px solid rgba(255, 183, 77, 0.25);
    color: #ffb74d;

    .v-icon {
      color: #ffb74d;
    }
  }
}

/* Modal */
.modal {
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  overflow: hidden;

  &--danger {
    border-color: rgba(244, 67, 54, 0.3);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);

  .header-icon {
    color: #ef5350;
  }
}

.modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.modal-content {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-message {
  font-size: 0.9rem;
  color: rgba(220, 232, 255, 0.85);
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.form-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
}

.form-textarea {
  padding: 10px 12px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  outline: none;
  font-family: inherit;
  resize: vertical;

  &:focus {
    border-color: rgba(120, 163, 255, 0.4);
  }
}

@media (max-width: 900px) {
  .item-cols {
    grid-template-columns: 60px 1fr 100px 130px;
  }
  .item-cols .col:nth-child(5),
  .item-cols .col:nth-child(6),
  .item-cols .col:nth-child(7) {
    display: none;
  }
  .snapshot-pair {
    grid-template-columns: 1fr;
  }
}
</style>
